import React, { useId, useState } from "react";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { login } from "../../auth/authSlice";
import { enqueueSnackbar } from "notistack";
import LoadingSpinner from "../loadingSpinner/LoadingSpinner";
import { API_BASE_URL } from "../../constants";
import PrimaryButton from "../../UI/PrimaryButton";
import { useNavigate } from "react-router-dom";

const LoginForm = ({ toggleForm }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const emailId = useId();
  const passwordId = useId();

  const { t } = useTranslation();

  const dispatch = useDispatch();

  const handleLogin = async (event) => {
    event.preventDefault();

    setLoading(true);
    try {
      const loginResponse = await fetch(`${API_BASE_URL}/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
      });

      if (!loginResponse.ok) {
        const errorData = await loginResponse.json();
        enqueueSnackbar(errorData.message, { variant: "error" });

        return;
      }

      const loginData = await loginResponse.json();

      localStorage.setItem("token", loginData.token);
      enqueueSnackbar("You are logged in!", { variant: "success" });
      // Fetch user profile
      const userResponse = await fetch(`${API_BASE_URL}/user`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${loginData.token}`,
        },
      });

      if (!userResponse.ok) {
        const userErrorData = await userResponse.json();
        enqueueSnackbar(userErrorData.message, { variant: "error" });
        return;
      }

      const userData = await userResponse.json();
      dispatch(login(userData));

      toggleForm(null);
      navigate("/home");

    } catch (error) {
      enqueueSnackbar("Somthing goes wrong. Check the connection!", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-10 z-50"
      onClick={() => toggleForm(null)}
    >
      <div
        className="bg-white p-8 rounded-lg shadow-2xl max-w-md w-full relative m-2"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={() => toggleForm(null)}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 focus:outline-none"
          aria-label={t("close")}
        >
          ✕
        </button>
        <h1
          id="login-form-title"
          className="text-gray-800 text-2xl font-bold text-center mb-6"
        >
          {t("login")}
        </h1>

        <form onSubmit={handleLogin} className="space-y-6">
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-600 mb-2"
            >
              {t("email")}
            </label>
            <input
              type="email"
              id={emailId}
              placeholder={t("enterYourEmail")}
              className="w-full p-4 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-darkGreen"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="relative">
            <label
              htmlFor="password"
              className="block text-sm font-medium text-gray-600 mb-2"
            >
              {t("password")}
            </label>
            <input
              type={showPassword ? "text" : "password"}
              id={passwordId}
              placeholder={t("enterYourPassword")}
              className="w-full p-4 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-darkGreen"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <button
              type="button"
              onClick={togglePasswordVisibility}
              className="absolute inset-y-0 right-0 top-6 flex items-center pr-3 text-gray-400 hover:text-darkGrering-darkGreen focus:outline-none"
              aria-label={showPassword ? t("hidePassword") : t("showPassword")}
            >
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>
          <div>
            <PrimaryButton
              text={loading ? <LoadingSpinner /> : t("login")}
              type="submit"
              className={
                loading
                  ? "opacity-75 cursor-not-allowed flex justify-center items-center"
                  : ""
              }
              disabled={loading}
            />
          </div>
        </form>

        <p className="text-gray-500 text-center mt-4">
          {t("dontHaveAccount")}{" "}
          <span
            onClick={() => toggleForm("register")}
            className=" underline cursor-pointer"
          >
            {t("signUp")}
          </span>
        </p>
        <p className="text-gray-500 text-center mt-4">
          {t("forgetPassword")}{" "}
          <span
            onClick={() => toggleForm("reset")}
            className=" underline cursor-pointer"
          >
            {t("reset")}
          </span>
        </p>
      </div>
    </div>
  );
};

export default LoginForm;
